// The platform operator's review console (`super_admin/reviews/`).
//
// `srev` rather than `rev`, the same split the affiliate module makes between `aff` and
// `saff`: the practitioner and the operator are told the same refusal in different words,
// because "you have already replied" and "this practice has already replied" are different
// sentences about the same 409.
//
// The program copy states CONSEQUENCES, not fields. `started_at` decides which in-person
// appointments the completion sweeper may close, so moving it backwards mass-completes
// history and mails a review request for each — the hint says so, and the server refuses
// anything below `seeded_at` outright.
export default {
  fr: {
    "nav.reviews": "Avis",

    "srev.title": "Avis & modération",
    "srev.subtitle": "Les avis publiés sur le marketplace, et les réglages du programme.",
    "srev.loading": "Chargement…",

    "srev.tab.reviews": "Avis",
    "srev.tab.settings": "Programme",

    "srev.list.title": "Avis par cabinet",
    "srev.list.empty": "Aucun cabinet n'a d'avis correspondant à ces filtres.",
    "srev.list.emptySearch": "Aucun cabinet avec des avis ne correspond à cette recherche.",

    "srev.practice.pageInfo": "Cabinets {from}–{to} sur {total}",
    "srev.practice.deleted": "Cabinet supprimé",
    "srev.practice.public": "{avg} · avis publics : {n}",
    "srev.practice.noPublic": "Pas encore de note publique",
    "srev.practice.total": "Avis au total : {n}",
    "srev.practice.openFlags": "Signalés, en attente de décision : {n}",
    "srev.practice.hidden": "Masqués : {n}",
    "srev.practice.matching": "{matching} sur {total} correspondent aux filtres",
    "srev.practice.latest": "Dernier avis le {date}",
    "srev.practice.marketplace": "Page marketplace",

    "srev.filter.practice": "Rechercher un cabinet",
    "srev.filter.practicePlaceholder": "Nom ou adresse du cabinet",
    "srev.filter.search": "Filtrer",
    "srev.filter.hidden": "Masqués",
    "srev.filter.flagged": "Signalés",
    "srev.filter.any": "tous",
    "srev.filter.yes": "oui",
    "srev.filter.no": "non",

    "srev.row.aria": "{avg} sur 5 étoiles",
    "srev.row.hidden": "Masqué",
    "srev.row.flagged": "Signalé",
    "srev.row.flagReason": "Signalement du cabinet : {reason}",
    "srev.row.hiddenReason": "Masqué par {by} — {reason}",
    "srev.row.reviewer": "Auteur :",
    "srev.row.client": "E-mail du client :",
    "srev.row.service": "Prestation :",

    "srev.hide": "Masquer",
    "srev.unhide": "Réafficher",
    "srev.hidden": "Avis masqué ; les moyennes ont été recalculées.",
    "srev.unhidden": "Avis réaffiché ; les moyennes ont été recalculées.",

    "srev.hideDialog.title": "Masquer cet avis",
    "srev.hideDialog.desc": "L'avis disparaît des pages publiques et des moyennes, mais reste visible pour le cabinet concerné. Le motif est enregistré dans le journal d'audit.",
    "srev.hideDialog.placeholder": "Motif de la décision…",
    "srev.hideDialog.confirm": "Masquer l'avis",
    "srev.hideDialog.submitting": "Traitement…",

    "srev.page.info": "{from}–{to} sur {total}",
    "srev.page.prev": "Précédent",
    "srev.page.next": "Suivant",

    "srev.program.title": "Programme d'avis",
    "srev.program.desc": "Ces réglages s'appliquent à toute la plateforme.",
    "srev.program.enabled": "Programme actif",
    "srev.program.enabledHint": "Désactiver met en pause la création des demandes et le dépôt des avis. Les avis déjà publiés restent visibles.",
    "srev.program.startedAt": "Actif depuis le",
    "srev.program.startedAtHint": "Seuls les rendez-vous en cabinet dont le créneau se termine à partir de cette date peuvent être clôturés automatiquement. Reculer cette date clôturerait l'historique et enverrait une demande d'avis pour chaque rendez-vous passé.",
    "srev.program.priorWeight": "Poids de l'a priori (m)",
    "srev.program.priorWeightHint": "Nombre d'avis fictifs, à la moyenne de la plateforme, ajoutés au classement de chaque cabinet. Plus il est élevé, plus un cabinet doit accumuler d'avis pour se démarquer. De 1 à 50.",
    "srev.program.tokenTtl": "Validité du lien (jours)",
    "srev.program.tokenTtlHint": "Durée pendant laquelle le lien reçu par courriel permet de déposer un avis. De 1 à 90.",
    "srev.program.seededAt": "Programme initialisé le {at} — la date d'activation ne peut pas être antérieure.",
    "srev.program.saved": "Programme enregistré.",
    "srev.program.saving": "Enregistrement…",

    "srev.error.REVIEWS_DISABLED": "Le programme d'avis est suspendu : cette action est indisponible.",
    "srev.error.REVIEW_TOKEN_UNKNOWN": "Ce jeton d'avis ne correspond à aucune demande.",
    "srev.error.REVIEW_TOKEN_EXPIRED": "Ce jeton d'avis a expiré.",
    "srev.error.REVIEW_ALREADY_SUBMITTED": "Un avis existe déjà pour ce rendez-vous.",
    "srev.error.REVIEW_INVALID": "Cette saisie n'est pas valide.",
    "srev.error.REVIEW_NOT_FOUND": "Cet avis est introuvable.",
    "srev.error.REVIEW_ALREADY_REPLIED": "Ce cabinet a déjà répondu à cet avis.",
    "srev.error.REVIEW_ALREADY_FLAGGED": "Ce cabinet a déjà signalé cet avis.",
    "srev.error.REVIEW_ALREADY_HIDDEN": "Cet avis est déjà masqué.",
    "srev.error.REVIEW_NOT_HIDDEN": "Cet avis n'est pas masqué.",
    "srev.error.REVIEW_PROGRAM_INVALID": "Ces réglages sont hors limites : vérifiez la date d'activation, le poids de l'a priori et la validité du lien.",
    "srev.error.PRACTITIONER_NOT_FOUND": "Ce cabinet est introuvable.",
    "srev.error.RATE_LIMITED": "Trop de requêtes : réessayez dans un instant.",
    "srev.error.UNKNOWN": "Une erreur est survenue et nous ne pouvons pas en dire plus.",
  },
  en: {
    "nav.reviews": "Reviews",

    "srev.title": "Reviews & moderation",
    "srev.subtitle": "The reviews published on the marketplace, and the program's settings.",
    "srev.loading": "Loading…",

    "srev.tab.reviews": "Reviews",
    "srev.tab.settings": "Program",

    "srev.list.title": "Reviews by practice",
    "srev.list.empty": "No practice has a review matching these filters.",
    "srev.list.emptySearch": "No practice with reviews matches this search.",

    "srev.practice.pageInfo": "Practices {from}–{to} of {total}",
    "srev.practice.deleted": "Practice deleted",
    "srev.practice.public": "{avg} · public reviews: {n}",
    "srev.practice.noPublic": "No public rating yet",
    "srev.practice.total": "Reviews in total: {n}",
    "srev.practice.openFlags": "Flagged, awaiting a decision: {n}",
    "srev.practice.hidden": "Hidden: {n}",
    "srev.practice.matching": "{matching} of {total} match the filters",
    "srev.practice.latest": "Latest review {date}",
    "srev.practice.marketplace": "Marketplace page",

    "srev.filter.practice": "Search a practice",
    "srev.filter.practicePlaceholder": "Practice name or address",
    "srev.filter.search": "Filter",
    "srev.filter.hidden": "Hidden",
    "srev.filter.flagged": "Flagged",
    "srev.filter.any": "any",
    "srev.filter.yes": "yes",
    "srev.filter.no": "no",

    "srev.row.aria": "{avg} out of 5 stars",
    "srev.row.hidden": "Hidden",
    "srev.row.flagged": "Flagged",
    "srev.row.flagReason": "Practice's report: {reason}",
    "srev.row.hiddenReason": "Hidden by {by} — {reason}",
    "srev.row.reviewer": "Reviewer:",
    "srev.row.client": "Client email:",
    "srev.row.service": "Service:",

    "srev.hide": "Hide",
    "srev.unhide": "Unhide",
    "srev.hidden": "Review hidden; the averages have been recomputed.",
    "srev.unhidden": "Review restored; the averages have been recomputed.",

    "srev.hideDialog.title": "Hide this review",
    "srev.hideDialog.desc": "The review leaves the public pages and the averages, but stays visible to the practice it is about. The reason is written to the audit log.",
    "srev.hideDialog.placeholder": "Reason for the decision…",
    "srev.hideDialog.confirm": "Hide the review",
    "srev.hideDialog.submitting": "Working…",

    "srev.page.info": "{from}–{to} of {total}",
    "srev.page.prev": "Previous",
    "srev.page.next": "Next",

    "srev.program.title": "Review program",
    "srev.program.desc": "These settings apply to the whole platform.",
    "srev.program.enabled": "Program active",
    "srev.program.enabledHint": "Turning it off pauses both the creation of requests and the submission of reviews. Reviews already published stay visible.",
    "srev.program.startedAt": "Active since",
    "srev.program.startedAtHint": "Only in-person appointments whose slot ends on or after this date may be closed automatically. Moving this date back would close historical appointments and mail a review request for every one of them.",
    "srev.program.priorWeight": "Prior weight (m)",
    "srev.program.priorWeightHint": "Number of imaginary reviews, at the platform average, added to every practice's ranking. The higher it is, the more reviews a practice needs to stand out. 1 to 50.",
    "srev.program.tokenTtl": "Link validity (days)",
    "srev.program.tokenTtlHint": "How long the emailed link can be used to leave a review. 1 to 90.",
    "srev.program.seededAt": "Program seeded on {at} — the activation date cannot be earlier.",
    "srev.program.saved": "Program saved.",
    "srev.program.saving": "Saving…",

    "srev.error.REVIEWS_DISABLED": "The review program is paused: this action is unavailable.",
    "srev.error.REVIEW_TOKEN_UNKNOWN": "This review token matches no request.",
    "srev.error.REVIEW_TOKEN_EXPIRED": "This review token has expired.",
    "srev.error.REVIEW_ALREADY_SUBMITTED": "A review already exists for this appointment.",
    "srev.error.REVIEW_INVALID": "This input is not valid.",
    "srev.error.REVIEW_NOT_FOUND": "This review no longer exists.",
    "srev.error.REVIEW_ALREADY_REPLIED": "This practice has already replied to this review.",
    "srev.error.REVIEW_ALREADY_FLAGGED": "This practice has already flagged this review.",
    "srev.error.REVIEW_ALREADY_HIDDEN": "This review is already hidden.",
    "srev.error.REVIEW_NOT_HIDDEN": "This review is not hidden.",
    "srev.error.REVIEW_PROGRAM_INVALID": "These settings are out of bounds: check the activation date, the prior weight and the link validity.",
    "srev.error.PRACTITIONER_NOT_FOUND": "This practice cannot be found.",
    "srev.error.RATE_LIMITED": "Too many requests — try again in a moment.",
    "srev.error.UNKNOWN": "Something went wrong and we cannot say what.",
  },
  es: {
    "nav.reviews": "Opiniones",

    "srev.title": "Opiniones y moderación",
    "srev.subtitle": "Las opiniones publicadas en el marketplace y los ajustes del programa.",
    "srev.loading": "Cargando…",

    "srev.tab.reviews": "Opiniones",
    "srev.tab.settings": "Programa",

    "srev.list.title": "Opiniones por consultorio",
    "srev.list.empty": "Ningún consultorio tiene opiniones que coincidan con estos filtros.",
    "srev.list.emptySearch": "Ningún consultorio con opiniones coincide con esta búsqueda.",

    "srev.practice.pageInfo": "Consultorios {from}–{to} de {total}",
    "srev.practice.deleted": "Consultorio eliminado",
    "srev.practice.public": "{avg} · opiniones públicas: {n}",
    "srev.practice.noPublic": "Todavía sin valoración pública",
    "srev.practice.total": "Opiniones en total: {n}",
    "srev.practice.openFlags": "Señaladas, pendientes de decisión: {n}",
    "srev.practice.hidden": "Ocultas: {n}",
    "srev.practice.matching": "{matching} de {total} coinciden con los filtros",
    "srev.practice.latest": "Última opinión el {date}",
    "srev.practice.marketplace": "Página del marketplace",

    "srev.filter.practice": "Buscar un consultorio",
    "srev.filter.practicePlaceholder": "Nombre o dirección del consultorio",
    "srev.filter.search": "Filtrar",
    "srev.filter.hidden": "Ocultas",
    "srev.filter.flagged": "Señaladas",
    "srev.filter.any": "todas",
    "srev.filter.yes": "sí",
    "srev.filter.no": "no",

    "srev.row.aria": "{avg} de 5 estrellas",
    "srev.row.hidden": "Oculta",
    "srev.row.flagged": "Señalada",
    "srev.row.flagReason": "Aviso del consultorio: {reason}",
    "srev.row.hiddenReason": "Ocultada por {by} — {reason}",
    "srev.row.reviewer": "Autor:",
    "srev.row.client": "Correo del cliente:",
    "srev.row.service": "Servicio:",

    "srev.hide": "Ocultar",
    "srev.unhide": "Mostrar de nuevo",
    "srev.hidden": "Opinión ocultada; las medias se han recalculado.",
    "srev.unhidden": "Opinión restaurada; las medias se han recalculado.",

    "srev.hideDialog.title": "Ocultar esta opinión",
    "srev.hideDialog.desc": "La opinión desaparece de las páginas públicas y de las medias, pero sigue visible para el consultorio afectado. El motivo se registra en el registro de auditoría.",
    "srev.hideDialog.placeholder": "Motivo de la decisión…",
    "srev.hideDialog.confirm": "Ocultar la opinión",
    "srev.hideDialog.submitting": "Procesando…",

    "srev.page.info": "{from}–{to} de {total}",
    "srev.page.prev": "Anterior",
    "srev.page.next": "Siguiente",

    "srev.program.title": "Programa de opiniones",
    "srev.program.desc": "Estos ajustes se aplican a toda la plataforma.",
    "srev.program.enabled": "Programa activo",
    "srev.program.enabledHint": "Desactivarlo pausa tanto la creación de solicitudes como el envío de opiniones. Las opiniones ya publicadas siguen visibles.",
    "srev.program.startedAt": "Activo desde el",
    "srev.program.startedAtHint": "Solo las citas presenciales cuyo horario termina en esta fecha o después pueden cerrarse automáticamente. Retrasar esta fecha cerraría el historial y enviaría una solicitud de opinión por cada cita pasada.",
    "srev.program.priorWeight": "Peso del a priori (m)",
    "srev.program.priorWeightHint": "Número de opiniones ficticias, con la media de la plataforma, añadidas a la clasificación de cada consultorio. Cuanto más alto, más opiniones necesita un consultorio para destacar. De 1 a 50.",
    "srev.program.tokenTtl": "Validez del enlace (días)",
    "srev.program.tokenTtlHint": "Tiempo durante el cual el enlace enviado por correo permite dejar una opinión. De 1 a 90.",
    "srev.program.seededAt": "Programa inicializado el {at}: la fecha de activación no puede ser anterior.",
    "srev.program.saved": "Programa guardado.",
    "srev.program.saving": "Guardando…",

    "srev.error.REVIEWS_DISABLED": "El programa de opiniones está en pausa: esta acción no está disponible.",
    "srev.error.REVIEW_TOKEN_UNKNOWN": "Este token de opinión no corresponde a ninguna solicitud.",
    "srev.error.REVIEW_TOKEN_EXPIRED": "Este token de opinión ha caducado.",
    "srev.error.REVIEW_ALREADY_SUBMITTED": "Ya existe una opinión para esta cita.",
    "srev.error.REVIEW_INVALID": "Esta entrada no es válida.",
    "srev.error.REVIEW_NOT_FOUND": "Esta opinión ya no existe.",
    "srev.error.REVIEW_ALREADY_REPLIED": "Este consultorio ya ha respondido a esta opinión.",
    "srev.error.REVIEW_ALREADY_FLAGGED": "Este consultorio ya ha señalado esta opinión.",
    "srev.error.REVIEW_ALREADY_HIDDEN": "Esta opinión ya está oculta.",
    "srev.error.REVIEW_NOT_HIDDEN": "Esta opinión no está oculta.",
    "srev.error.REVIEW_PROGRAM_INVALID": "Estos ajustes están fuera de los límites: revise la fecha de activación, el peso del a priori y la validez del enlace.",
    "srev.error.PRACTITIONER_NOT_FOUND": "No se encuentra este consultorio.",
    "srev.error.RATE_LIMITED": "Demasiadas solicitudes: vuelva a probar en un momento.",
    "srev.error.UNKNOWN": "Ha ocurrido un error y no podemos decir más.",
  },
};
